import {
  ChangeDetectionStrategy,
  Component,
  input,
  InputSignal,
  OnChanges,
  output,
  OutputEmitterRef,
  signal,
  SimpleChanges,
  WritableSignal,
} from "@angular/core";
import { TranslocoModule } from "@ngneat/transloco";
import { PaginationButtonComponent } from "./pagination-button/pagination-button.component";
import { SvgIconComponent } from "../social-icons/svg-icon/svg-icon.component";

@Component({
  selector: "app-pagination",
  imports: [PaginationButtonComponent, SvgIconComponent, TranslocoModule],
  templateUrl: "./pagination.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: "./pagination.component.scss",
})
export class PaginationComponent implements OnChanges {
  public readonly totalPages: InputSignal<number> = input<number>(0);
  public readonly pageChange: OutputEmitterRef<number> = output<number>();

  public readonly EDGE_PAGE_COUNT: number = 4;
  public readonly currentPage: WritableSignal<number> = signal<number>(1);
  public readonly pages: WritableSignal<number[]> = signal<number[]>([]);
  public readonly leftPages: WritableSignal<number[]> = signal<number[]>([]);
  public readonly rightPages: WritableSignal<number[]> = signal<number[]>([]);
  public readonly showDots: WritableSignal<boolean> = signal<boolean>(false);

  // Must keep emitting pageChange(1) on every totalPages change, including the first binding:
  // consumers load page 1 from it.
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes["totalPages"]) {
      this.currentPage.set(1);
      this._initializePages();
      this.pageChange.emit(this.currentPage());
    }
  }

  public handlePageChange(selectedPage: number): void {
    if (selectedPage < 1 || selectedPage > this.totalPages()) return;

    this.currentPage.set(selectedPage);
    this.pageChange.emit(selectedPage);
  }

  private _initializePages(): void {
    const totalPages: number = this.totalPages();
    const pages: number[] = Array.from({ length: totalPages }, (_: unknown, i: number) => i + 1);

    this.pages.set(pages);
    this.leftPages.set(pages.slice(0, Math.min(this.EDGE_PAGE_COUNT, totalPages)));
    this.rightPages.set(
      totalPages > this.EDGE_PAGE_COUNT
        ? pages.slice(Math.max(this.EDGE_PAGE_COUNT, totalPages - this.EDGE_PAGE_COUNT), totalPages)
        : [],
    );
    this.showDots.set(totalPages > this.EDGE_PAGE_COUNT * 2);
  }
}
